// External libraries
import {
  capitalizeFirst,
  formatExpiryForBackend,
  getDateOfBirthOrder,
  getDateOfBirthSeparator,
  hasOwnProp,
  isObject,
  padLeft,
  parseByDateOfBirthSeparator,
  upperCase,
} from '@checkout/react-components';

// Internal modules
import { appStore } from '@/store';
import { $logger } from '@/lib/logger';
import { FPTI_FOCUS_AND_BLUR_EVENT_PAYLOAD } from './fpti';
import {
  features,
  getAddressMetaKey,
  getContentHash,
  getFieldError,
  getLocale,
  getToken,
} from '@/lib';
import { isConversionNeeded } from '../PaymentInformation/utils';
import { isConversionChangeable } from '../PaymentInformation/CardFields/CurrencyConversion';
import { UI_STATE_FIELDS_TO_FORM_STATE_MAP } from './constants';

const shouldDisplayErrorOnChange = (id, fieldState, meta) => {
  const { errors, isValid } = meta.validation;

  if (isValid) {
    return false;
  }

  const { displayError } = fieldState;
  const {
    api: { allowedCardTypes },
  } = appStore.getState();
  const firstError = errors[0];

  /**
   * If the field that is being changed is the card number and we
   * don't have the allowed card types yet, we don't want to
   * display the error when the value hits the max length for certain
   * error codes (invalidType, tooLong, tooShort) because we can't
   * accurately validate those until we have the allowed card types.
   */
  if (
    id === 'cardNumber' &&
    !allowedCardTypes.length &&
    ['invalidType', 'tooShort', 'tooLong'].includes(firstError)
  ) {
    return displayError;
  }

  // Wrapping `isMaxLength` in `Boolean()` because it can be `undefined`
  // and I'd like the return value of this function to always be a boolean
  return displayError || Boolean(meta.isMaxLength);
};

const stripAddressPrefix = id => {
  return id.replace(/^(billing|shipping)(\w)/, (_, __, l) => l.toLowerCase());
};

/**
 * @function getFieldCurrentState
 * @param {object} params | An object
 * @param {string} params.field | field from formState
 * @param {string} params.addressType | billing or shipping address
 * @returns {object}
 */
const getFieldCurrentState = ({ field, addressType }) => {
  const { form: formState } = appStore.getState();
  const currentState = addressType
    ? formState[`${addressType}Address`][field]
    : formState[field];

  return currentState;
};

/**
 * @function getFieldData
 * @param {object} target | event.target object
 * @param {object} meta | metadata object passed to change handler
 * @returns {object}
 */
const getFieldData = ({ id, name, type }, { addressType }) => {
  const isRadio = type === 'radio';
  const field = isRadio ? name : stripAddressPrefix(id);
  const currentState = getFieldCurrentState({ addressType, field });

  return {
    addressType: addressType && `${addressType}Address`,
    currentState,
    field,
  };
};

const getAddressMetaFromId = id => {
  const addressRegex = /^billing|^shipping/;
  const match = id.match(addressRegex);

  return {
    addressType: match && match[0],
    isAddressField: addressRegex.test(id),
  };
};

/**
 * @function getForcedValidCopy
 * @param {object} fieldGroup
 * @returns {object}
 * @description Creates a copy of a fieldGroup (e.g. billingAddress)
 *  and sets all `isValid` keys to `true`. This is needed for when
 *  the user can't edit the address fields to fix any validation errors.
 */
const getForcedValidCopy = fieldGroup => {
  const entries = Object.entries(fieldGroup);

  return entries.reduce((accum, [key, data]) => {
    if (!isObject(data)) {
      accum[key] = data;
      return accum;
    }

    accum[key] = {
      ...data,
      ...(hasOwnProp(data, 'isValid') && { isValid: true }),
    };
    return accum;
  }, {});
};

/**
 * @function getShippingAddressFromBillingAddress
 * @param {object} paramsObj {billingAddress, firstName, lastName}
 * @returns {object}
 * @description creates a "valid" copy of the billing address with
 *  the additional fields firstName, lastName, and country. This is
 *  then used as the shipping address in certain situations, shipToBilling
 *  and when we don't allow shipping address to be changed but no shipping
 *  address was sent by the merchant. The result is "valid" in the sense
 *  that it won't go through any client side validations. If the billing
 *  address is invalid it will fail validations and the user will have to
 *  correct the errors and then resubmit the form. At that point we'll
 *  copy over the address again. We do this because since we aren't showing
 *  the shipping fields the user isn't able to correct any errors, so we don't
 *  want to tag any field as invalid.
 */
const getShippingAddressFromBillingAddress = ({
  billingAddress,
  firstName,
  lastName,
}) => ({
  accountQuality: billingAddress.accountQuality,
  country: { value: getLocale().country },
  firstName: { value: firstName.value },
  lastName: { value: lastName.value },
  ...getForcedValidCopy(billingAddress),
});

/**
 * @function filterOutNonApplicableAddressFields
 * @param {object} address
 * @param {string} locale
 * @returns {object}
 * @description Takes the address object from the store and returns a new
 *  object with only the fields that apply to the country the address is for
 *  and only fields that are not empty or are empty but are required.
 */
const filterOutNonApplicableAddressFields = (address, addressMetaKey) => {
  const { api: apiState } = appStore.getState();
  const { address: addressMetadata } = apiState.griffinMetadata;
  const { layout = [] } = addressMetadata[addressMetaKey] || {};

  if (!layout.length) {
    $logger.info('weasley_address_field_filter_missing_layout', {
      addressType: hasOwnProp(address, 'country') ? 'shipping' : 'billing',
      keyRequested: addressMetaKey,
      keysAvailable: Object.keys(addressMetadata),
    });
  }

  return layout.reduce((accum, { name, isRequired }) => {
    const fieldHasValue = Boolean(address[name]?.value?.length);
    if (fieldHasValue || isRequired) {
      accum[name] = address[name];
    }
    return accum;
  }, {});
};

/**
 * @function getCommonFormState
 * @param {object} formState
 * @returns {object}
 * @description Takes formState and returns an object containing all the
 *  values that are common to both guests and signup.
 */
const getCommonFormState = formState => {
  const {
    dialingCode,
    email,
    firstName,
    lastName,
    onboardOption,
    phone,
    phoneType,
  } = formState;

  return {
    dialingCode,
    email,
    firstName,
    lastName,
    onboardOption,
    phone,
    phoneType,
  };
};

/**
 * @function getBankState
 * @param {object} formState
 * @returns {object}
 * @description Takes formState and returns an object containing
 * bank fields.
 */
const getBankState = formState => {
  const { ui: uiState } = appStore.getState();
  const { fiType } = uiState;
  const {
    bankAccountNumber,
    bankAccountType,
    bankIban,
    bankSortCode,
  } = formState;

  if (fiType === 'card') {
    return {};
  }

  const bankState = {};
  if (bankAccountType.value === 'bank') {
    bankState.bankAccountNumber = bankAccountNumber;
    bankState.bankSortCode = bankSortCode;
  } else {
    bankState.bankIban = bankIban;
  }

  return {
    bankAccountType,
    ...bankState,
  };
};

/**
 * @function getCardState
 * @param {object} formState
 * @returns {object}
 * @description Takes formState and returns an object containing
 * card fields.
 */
const getCardState = formState => {
  const { ui: uiState } = appStore.getState();
  const { fiType } = uiState;

  if (fiType === 'bank') {
    return {};
  }

  // Only include conversion selection data if it is needed
  const withConversion = isConversionNeeded() && isConversionChangeable();
  const {
    cardCvv,
    cardExpiry,
    cardNumber,
    currencyConversionSelection,
    cardProductClass,
  } = formState;

  return {
    cardCvv,
    cardExpiry,
    cardNumber,
    ...(features.isActive('shouldCollectCardProductClass') && {
      cardProductClass,
    }),
    ...(withConversion && { currencyConversionSelection }),
  };
};

/**
 * @function getAccountQualityMetaDataFromAddress
 * @param {object} address
 * @returns {object}
 * @description Pull account quality values from addresses in form state and
 * construct the accountQuality object for form submission.
 */
const getAccountQualityMetaDataFromAddress = address => {
  return {
    accountQuality: {
      autoCompleteType: address?.autoCompleteType?.value,
      isUserModified: address?.isManipulated?.value,
    },
  };
};

/**
 * @function getInstallmentTermState
 * @param {object} formState
 * @returns {object}
 * @description Takes formState and returns an object containing
 * installment term.
 */
const getInstallmentTermState = formState => {
  const { ui: uiState } = appStore.getState();
  const { isInstallmentButtonShown } = uiState;
  const { installmentTerm } = formState;

  if (!isInstallmentButtonShown) {
    return {};
  }

  return {
    installmentTerm,
  };
};

/**
 * @function filterFormState
 * @param {object} formState
 * @returns {object}
 * @description filters address fields out of the form state that
 *  shouldn't be validated or submitted because they don't apply to
 *  either the flow (isAddressless, !isAllowChangeShipping) or the country
 *  (e.g. no postal code for HK)
 */
const filterFormState = formState => {
  const {
    billingAddress,
    firstName,
    lastName,
    shippingAddress,
    phone,
  } = formState;
  const { ui: uiState } = appStore.getState();
  const filteredFormState = {
    ...getCommonFormState(formState),
    ...getBankState(formState),
    ...getCardState(formState),
    ...getInstallmentTermState(formState),
    billingAddress: {
      ...filterOutNonApplicableAddressFields(
        billingAddress,
        getAddressMetaKey(),
      ),
      accountQuality: {
        ...getAccountQualityMetaDataFromAddress(billingAddress).accountQuality,
        // SCA phone confimration otp code
        twoFactorPhoneVerificationId: phone.meta?.verificationId,
      },
    },
    shippingAddress: {
      country: shippingAddress.country,
      firstName: shippingAddress.firstName,
      lastName: shippingAddress.lastName,
      ...filterOutNonApplicableAddressFields(
        shippingAddress,
        getAddressMetaKey(shippingAddress.country.value),
      ),
      ...getAccountQualityMetaDataFromAddress(shippingAddress),
    },
  };

  const { fiType } = uiState;
  // Do not update cardFieldsToDisplay for bank fiType.
  if (fiType === 'card') {
    // Add or remove the fields into filteredFormState based on
    // fields displayed with card - uiState.cardFieldsToDisplay
    Object.keys(uiState.cardFieldsToDisplay).forEach(key => {
      const formStateField = UI_STATE_FIELDS_TO_FORM_STATE_MAP[key];
      if (!uiState.cardFieldsToDisplay[key] && formStateField) {
        if (filteredFormState[formStateField]) {
          delete filteredFormState[formStateField];
        }
      } else if (uiState.cardFieldsToDisplay[key] && formStateField) {
        // DOB is displayed with card fields even for guest when card type
        // is Carte Aurore, Cofinoga ou privilege, 4 etoiles
        filteredFormState[formStateField] = formState[formStateField];
      }
    });
  }
  const isGuestEligible = features.isActive('isGuestEligible');
  const isPOMAEligible = features.isActive('isPOMAEligible');
  const onboardOption = formState.onboardOption.value;
  const isPOMAFlow = isPOMAEligible && onboardOption === 'signup';
  if (isGuestEligible && onboardOption === 'guest') {
    // if guest eligible and onboard option selected is guest
    filteredFormState.guestAgreeToTerms = formState.guestAgreeToTerms;
  } else if (onboardOption === 'signup' && !isPOMAFlow) {
    // forced sign up or onboard option is signup, but not POMA
    filteredFormState.password = formState.password;
    filteredFormState.signupAgreeToTerms = formState.signupAgreeToTerms;
    filteredFormState.marketingOptIn = formState.marketingOptIn;

    // adding the kyc fields
    const kycFields = features.config.kycFields;
    kycFields.forEach(field => {
      // We only add secondaryIdentityDocument if
      // countryOfResidence is Russia
      if (
        (field === 'SecondaryIdentityDocumentNumber' ||
          field === 'SecondaryIdentityDocumentType') &&
        formState.countryOfResidence.value !== 'RU'
      ) {
        return;
      }
      const formattedField = field[0].toLowerCase() + field.substring(1);
      filteredFormState[formattedField] = formState[formattedField];
    });
  }
  const hasSepaMandate = features.isActive('hasSepaMandate');
  const isBankFiType = uiState.fiType === 'bank';

  if (hasSepaMandate && isBankFiType) {
    filteredFormState.sepaMandate = formState.sepaMandate;
  }

  const { api: apiState } = appStore.getState();
  const { hasPrefillAddress } = apiState.prefillAddressMetadata;
  const isAddressless = features.isActive('isAddressless');
  if (isAddressless) {
    // Since we aren't showing the address fields we delete the
    // billing address from the form state
    delete filteredFormState.billingAddress;

    // If we were not sent a shipping address from the merchant
    // then we delete the shipping address from the form state
    if (!hasPrefillAddress) {
      delete filteredFormState.shippingAddress;
    } else {
      // Since the shipping address fields are not displayed the user will not
      // be able to fix any errors, so we set the validity to valid
      filteredFormState.shippingAddress = getForcedValidCopy(
        filteredFormState.shippingAddress,
      );
    }

    return filteredFormState;
  }

  const isAllowChangeShipping = features.isActive('isAllowChangeShipping');
  if (!isAllowChangeShipping) {
    if (hasPrefillAddress) {
      const { shippingAddress } = filteredFormState;

      return {
        ...filteredFormState,
        shippingAddress: {
          // Since the shipping address fields are not displayed the user will
          // not be able to fix any errors, so we set the validity to valid
          ...getForcedValidCopy(shippingAddress),
          firstName: {
            value: shippingAddress.value || firstName.value,
          },
          lastName: {
            value: shippingAddress.value || lastName.value,
          },
        },
      };
    }

    return {
      ...filteredFormState,
      shippingAddress: getShippingAddressFromBillingAddress({
        billingAddress: filteredFormState.billingAddress,
        firstName,
        lastName,
      }),
    };
  }

  // If we are shipping to the billing address we need to copy its values
  // over to the shippingAddress state field.
  const { value: shipToBilling } = formState.shipToBilling;
  if (shipToBilling) {
    return {
      ...filteredFormState,
      shippingAddress: getShippingAddressFromBillingAddress({
        billingAddress: filteredFormState.billingAddress,
        firstName,
        lastName,
      }),
    };
  }

  return filteredFormState;
};

const logBlurOrFocusEvent = e => {
  const {
    target: { id },
    type,
  } = e;

  const meta = getAddressMetaFromId(id);
  const { currentState } = getFieldData(e.target, meta);

  // If currentState is missing then return early
  // Eg: cardType is not having its own state and it is part of cardNumber
  if (!currentState) {
    $logger.warn(`${id}_field_${type}_event_state_not_found`);
    return;
  }

  const { errorCode, isValid } = currentState;
  const payload = FPTI_FOCUS_AND_BLUR_EVENT_PAYLOAD({
    errorCode,
    eventType: type,
    fieldName: id,
    isValid,
  });

  $logger.fpti(`${id}_field_${type}_event`, payload);
};

const mapAddressValueToName = (address, addressKey) => {
  const {
    api: { griffinMetadata },
  } = appStore.getState();
  const { layout } = griffinMetadata.address[addressKey];

  return layout.reduce((accum, { name }) => {
    if (hasOwnProp(address, name)) {
      // Pattern matching for the postal code field is not case sensitive
      // so we need to normalize it before we send it to the backend
      const value =
        name === 'postalCode'
          ? upperCase(address[name].value)
          : address[name].value;
      accum[name] = value.trim();
    }
    return accum;
  }, {});
};

const serializeAddressFields = data => {
  const addressFields = {};
  const { country } = getLocale();

  if (data.billingAddress) {
    addressFields.billingAddress = {
      ...mapAddressValueToName(data.billingAddress, getAddressMetaKey()),
      accountQuality: data.billingAddress.accountQuality,
      country,
      familyName: data.lastName.value?.trim(),
      givenName: data.firstName.value?.trim(),
    };
  }

  if (data.shippingAddress) {
    const { value: shipCountry } = data.shippingAddress.country;
    addressFields.shippingAddress = {
      ...mapAddressValueToName(
        data.shippingAddress,
        getAddressMetaKey(shipCountry),
      ),
      accountQuality: data.shippingAddress.accountQuality,
      country: shipCountry,
      familyName: data.shippingAddress.lastName.value?.trim(),
      givenName: data.shippingAddress.firstName.value?.trim(),
    };
  }

  return addressFields;
};

/**
 * @function stringToDateObject
 * @param {string} dateString date in form of string (ex. '1/12/1990')
 * @returns {object} returns object with string fields year, month, day
 * @description used in serializeFormData since dateOfBirth is stored as string
 *  but graphQL expects type DateOfBirth (object with fields year, month, day)
 */
const stringToDateObject = dateString => {
  const { dateMeta } = appStore.getState().api.griffinMetadata;
  const datePattern = dateMeta.datePattern;
  const separator = getDateOfBirthSeparator(datePattern);
  const dateOrder = getDateOfBirthOrder(datePattern, separator);
  const { day, month, year } = parseByDateOfBirthSeparator(
    dateOrder,
    dateString,
    separator,
  );

  // xobuyer requires a 2 digit day and month
  return {
    day: padLeft(day, 2, '0'),
    month: padLeft(month, 2, '0'),
    year,
  };
};

const getPomaFlowData = () => {
  const { country, language, locale } = getLocale();
  const contentHash = getContentHash(locale);

  // Hardcoded value at end identifies the content key that user agreed to
  // prettier-ignore
  const contentIdentifier =
    `${country}:${language}:${contentHash}:terms.poma`;

  return {
    contentIdentifier,
  };
};

/**
 * @function getSignupData
 * @param {object} formData
 * @returns {object}
 * @description Serializes the form data that is specific to the signup flow
 */
const getSignupData = formData => {
  const {
    marketingOptIn: { value: marketingOptIn },
    password: { value: password },
  } = formData;
  const { country, language, locale } = getLocale();
  const contentHash = getContentHash(locale);

  // Hardcoded value at end identifies the content key that user agreed to
  // prettier-ignore
  const contentIdentifier =
    `${country}:${language}:${contentHash}:compliance.signupTerms`;

  // Know Your Customer fields -- different countries require different
  // information to be collected. Examples - DOB, Nationality, Occupation, etc.
  // Filtering out identityDocumentNumber because we can combine the object in
  // the following reduce on identityDocumentType
  const kycFields = features.config.kycFields
    .filter(
      field =>
        field !== 'IdentityDocumentNumber' &&
        field !== 'SecondaryIdentityDocumentNumber',
    )
    .reduce((accum, field) => {
      const formattedField = field[0].toLowerCase() + field.substring(1);
      switch (formattedField) {
        case 'dateOfBirth': {
          const { value: dobString } = formData.dateOfBirth;
          const dateObject = stringToDateObject(dobString);
          return {
            ...accum,
            dateOfBirth: dateObject,
          };
        }
        case 'identityDocumentType': {
          const { value: identityDocumentType } = formData.identityDocumentType;
          const {
            value: identityDocumentNumber,
          } = formData.identityDocumentNumber;

          return {
            ...accum,
            identityDocument: {
              type: identityDocumentType,
              value: identityDocumentNumber,
            },
          };
        }
        case 'secondaryIdentityDocumentType': {
          // We only send secondaryIdentityDocument if
          // countryOfResidence is Russia
          if (formData.countryOfResidence?.value !== 'RU') {
            break;
          }

          const {
            value: secondaryIdentityDocumentType,
          } = formData.secondaryIdentityDocumentType;
          const {
            value: secondaryIdentityDocumentNumber,
          } = formData.secondaryIdentityDocumentNumber;

          return {
            ...accum,
            secondaryIdentityDocument: {
              type: secondaryIdentityDocumentType,
              value: secondaryIdentityDocumentNumber,
            },
          };
        }
        default: {
          return {
            ...accum,
            [formattedField]: formData[formattedField]?.value,
          };
        }
      }
    }, {});

  return {
    contentIdentifier,
    // xobuyer uses `marketingOptOut` because it requires an extra service call
    // after signup to opt out. The client calls it opt in because it's an
    // optional field the user has to explicitly check off.
    marketingOptOut: !marketingOptIn,
    password,
    ...kycFields,
  };
};

/**
 * @function getBankPayload
 * @param {object} data Form fields that have been filtered based on the flow
 * @returns {object} Returns the formatted graphql variables payload
 * for bank.
 */
const getBankPayload = data => {
  const { ui: uiState } = appStore.getState();
  const { fiType } = uiState;

  if (fiType === 'card') {
    return;
  }

  const {
    bankAccountNumber,
    bankAccountType: { value: bankAccountType },
    bankIban,
    bankSortCode,
  } = data;

  const bankPayload = {};
  if (bankAccountType === 'bank') {
    bankPayload.accountNumber = bankAccountNumber.value;
    bankPayload.routingNumber = bankSortCode.value;
  } else {
    bankPayload.iban = bankIban.value;
  }

  return {
    bank: {
      ...bankPayload,
    },
  };
};

/**
 * @function getCardPayload
 * @param {object} data Form fields that have been filtered based on the flow
 * @returns {object} Returns the formatted graphql variables payload
 * for card.
 */
const getCardPayload = data => {
  const { ui: uiState } = appStore.getState();
  const { fiType } = uiState;

  if (fiType === 'bank') {
    return;
  }

  const {
    cardCvv: { value: cardCvv } = {},
    cardExpiry: { value: cardExpiry } = {},
    cardNumber: { value: cardNumber, cardType },
    currencyConversionSelection: currencyConversionType,
    cardProductClass: { value: productClass } = {},
  } = data;

  return {
    card: {
      cardNumber,
      ...(cardExpiry && { expirationDate: formatExpiryForBackend(cardExpiry) }),
      ...(cardCvv && { securityCode: cardCvv }),
      ...(cardType && { type: cardType }),
      ...(features.isActive('shouldCollectCardProductClass') && {
        productClass,
      }),
    },
    ...(currencyConversionType && { currencyConversionType }),
  };
};

/**
 * @function serializeFormData
 * @param {object} data Form fields that have been filtered based on the flow
 * @returns {object} Returns the formatted graphql variables payload,
 *  including additions such as token, and formatted card, phone inputs
 */
const serializeFormData = data => {
  const { ui: uiState, form: formState } = appStore.getState();
  const {
    api: { griffinMetadata, installmentData },
  } = appStore.getState();
  const { value: onboardOption } = data.onboardOption;
  const isSignup = onboardOption === 'signup';
  const isPOMAEligible = features.isActive('isPOMAEligible');
  const isPOMAFlow = isPOMAEligible && isSignup;

  const dialingCodeMap = griffinMetadata.territoriesToDialingCodeMap;
  const dialingCode = dialingCodeMap[data.dialingCode.value];

  const {
    email: { value: email },
    firstName: { value: firstName },
    lastName: { value: lastName },
    phone: { value: number },
    phoneType: { value: phoneType },
  } = data;

  const installment = installmentData.find(
    ({ term }) => term === data?.installmentTerm?.value,
  );

  const {
    shareAddressWithDonatee: { value: shareAddressWithDonatee },
  } = formState;

  return {
    ...getBankPayload(data),
    ...getCardPayload(data),
    country: getLocale().country,
    ...(uiState.cardFieldsToDisplay.dob && {
      dateOfBirth: stringToDateObject(data.dateOfBirth.value),
    }),
    email: email.trim(),
    firstName: firstName.trim(),
    lastName: lastName.trim(),
    phone: {
      countryCode: `${dialingCode}`,
      number,
      type: phoneType,
    },
    ...(installment && formatInstallmentOption(installment)),
    ...(features.isActive('isDonation') && {
      shareAddressWithDonatee,
    }),
    supportedThreeDsExperiences: [features.get('threeDsV1Experience')],
    token: getToken(),
    ...serializeAddressFields(data),
    ...(!isPOMAFlow && isSignup && getSignupData(data)),
    ...(isPOMAFlow && getPomaFlowData()),
    ...(features.isActive('hasFreeReturnShipping') && {
      frsOptIn: true,
    }),
  };
};

/**
 * @function formatInstallmentOption
 * @param {object} data raw installment data fetched from the server
 * @returns {object} Returns the formmated installment option in
 *  InstallmentsInput type
 */
const formatInstallmentOption = data => {
  const { discount, monthlyPayment, term, totalCost } = data;
  // discount fields do not always exist, default to 0
  // other fields should always exist
  const discountValue = discount?.amount?.currencyValue || '0';
  const discountPercentage = discount?.percentage || '0';
  const {
    currencyCode: paymentCurrency,
    currencyValue: paymentValue,
  } = monthlyPayment;
  const {
    currencyCode: totalCostCurrency,
    currencyValue: totalCostValue,
  } = totalCost;

  return {
    selectedInstallmentOption: {
      discountCurrency: paymentCurrency,
      discountPercentage: discountPercentage,
      discountValue: discountValue,
      paymentCurrency,
      paymentValue,
      term,
      totalCostCurrency,
      totalCostValue,
    },
  };
};

/**
 * @function validateForm
 * @param {object} state
 * @returns {object} Object to merge with form state
 */
const validateForm = state => {
  const accum = { errorLogPayload: {}, formErrors: {} };
  const addressFields = /^(billing|shipping)Address$/;

  return Object.keys(state).reduce((errors, field) => {
    const isAddressField = addressFields.test(field);
    const fieldState = state[field];

    if (hasOwnProp(fieldState, 'isValid') && !fieldState.isValid) {
      const { errorCode, isValid, value } = fieldState;

      errors.formErrors[field] = {
        displayError: true,
        error: getFieldError({ errorCode, field, isValid, value }),
      };
      errors.errorLogPayload[field] = fieldState.errorCode;
    } else if (isAddressField) {
      const { formErrors, errorLogPayload } = validateForm(fieldState);
      const hasErrors = Object.keys(formErrors).length;

      if (hasErrors) {
        errors.formErrors[field] = formErrors;
        Object.entries(errorLogPayload).forEach(([fieldName, error]) => {
          const key = `${field}${capitalizeFirst(fieldName)}`;
          errors.errorLogPayload[key] = error;
        });
      }
    }

    return errors;
  }, accum);
};

/**
 * @function serializeOnboardingPlanData
 * @param {object} state
 * @returns {object} returns the formatted graphql payload.
 */
const serializeOnboardingPlanData = data => {
  const { api: apiState } = appStore.getState();
  const {
    cupOtpfundingInstrumentId,
    cupOtpEncryptedCardLoginAccountNumber,
  } = apiState;
  const {
    card: { securityCode: cardCvv },
    currencyConversionType,
  } = getCardPayload(data);
  const { shippingAddress } = serializeAddressFields(data);
  const token = getToken();
  const { onboardOption } = data;
  const isGuest = onboardOption === 'guest';
  const userType = isGuest ? 'GUEST' : 'SIGN_UP';

  return {
    ...(cupOtpEncryptedCardLoginAccountNumber && {
      encryptedCardLoginAccountNumber: cupOtpEncryptedCardLoginAccountNumber,
    }),
    ...(currencyConversionType && { currencyConversionType }),
    cvv: cardCvv,
    fundingInstrumentId: cupOtpfundingInstrumentId,
    shippingAddress,
    supportedThreeDsExperiences: [features.get('threeDsV1Experience')],
    token,
    userType,
  };
};

export {
  filterFormState,
  getCardState,
  getFieldData,
  getSignupData,
  getAddressMetaFromId,
  logBlurOrFocusEvent,
  mapAddressValueToName,
  serializeFormData,
  serializeOnboardingPlanData,
  shouldDisplayErrorOnChange,
  validateForm,
};
